import { ref, getDownloadURL, deleteObject } from 'firebase/storage';

const resizedUrlCache = new Map<string, string>();

/**
 * Returns the Firebase Resize Images extension URL for a given original download URL.
 * Derives the resized storage path (inserting `_800x800` before the extension), fetches
 * its download token via getDownloadURL, and caches the result for the session.
 * Falls back to the original URL if the resized version doesn't exist yet.
 */
export async function getResizedStorageUrl(originalUrl: string, suffix = '_800x800'): Promise<string> {
  if (resizedUrlCache.has(originalUrl)) return resizedUrlCache.get(originalUrl)!;
  try {
    const url = new URL(originalUrl);
    if (!url.hostname.includes('firebasestorage.googleapis.com')) return originalUrl;
    const marker = '/o/';
    const idx = url.pathname.indexOf(marker);
    if (idx === -1) return originalUrl;
    const decodedPath = decodeURIComponent(url.pathname.substring(idx + marker.length).split('?')[0]);
    const lastDot = decodedPath.lastIndexOf('.');
    if (lastDot === -1) return originalUrl;
    const resizedPath = decodedPath.slice(0, lastDot) + suffix + decodedPath.slice(lastDot);
    const resizedUrl = await getDownloadURL(ref(storage, resizedPath));
    resizedUrlCache.set(originalUrl, resizedUrl);
    return resizedUrl;
  } catch {
    return originalUrl;
  }
}
import { storage, auth } from '../firebase';
import { firebaseConfig } from '../config';
import { logger } from '../utils/logger';

/** REST upload response: token is under metadata.firebaseStorageDownloadTokens (GCS) or top-level downloadTokens (v0) */
interface StorageUploadResponse {
  metadata?: { firebaseStorageDownloadTokens?: string };
  downloadTokens?: string;
}

/**
 * Upload via fetch with explicit Authorization header (SDK was not sending it).
 * CORS must allow your app origin and Authorization (see storage.cors.json).
 */
export const uploadFile = async (path: string, file: File): Promise<string> => {
  if (!storage) {
    throw new Error('Firebase Storage is not initialized');
  }

  // Robust auth check before any Storage operation; prevents upload without a confirmed user.
  const user = auth?.currentUser ?? null;
  if (user === null) {
    console.error('User not authenticated for Cloud Storage upload.');
    throw new Error('You must be signed in to upload files. Please sign in again and try again.');
  }
  const bucket = firebaseConfig?.storageBucket;
  if (!bucket) {
    throw new Error('Storage bucket not configured');
  }
  const token = await user.getIdToken(true);
  const url = `https://firebasestorage.googleapis.com/v0/b/${bucket}/o?uploadType=media&name=${encodeURIComponent(path)}`;
  // If user is authenticated but you still get PERMISSION_DENIED / "Missing or insufficient permissions",
  // double-check Firebase Console → Storage → Rules: allow read, write: if request.auth != null;
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': file.type || 'application/octet-stream',
    },
    body: file,
  });
  if (!res.ok) {
    const errText = await res.text();
    if (res.status === 403 || errText.includes('PERMISSION_DENIED') || errText.includes('insufficient permissions')) {
      console.error(
        'Storage upload PERMISSION_DENIED despite authenticated user. ' +
          'Double-check Firebase Console → Storage → Rules: allow read, write: if request.auth != null;'
      );
    }
    throw new Error(`Storage upload failed: ${res.status} ${errText}`);
  }
  const data = (await res.json()) as StorageUploadResponse;
  const downloadToken = data?.metadata?.firebaseStorageDownloadTokens ?? data?.downloadTokens;
  if (!downloadToken) {
    const storageRef = ref(storage, path);
    return getDownloadURL(storageRef);
  }
  const encodedPath = encodeURIComponent(path);
  return `https://firebasestorage.googleapis.com/v0/b/${bucket}/o/${encodedPath}?alt=media&token=${downloadToken}`;
};

/**
 * Download a file signed in, by its path, without ever asking for a download link
 * (getDownloadURL would mint a public token). Used for inspection photos.
 */
export const fetchStorageBlob = async (path: string): Promise<Blob> => {
  const user = auth?.currentUser ?? null;
  const bucket = firebaseConfig?.storageBucket;
  if (!user || !bucket) throw new Error('Not signed in');
  const token = await user.getIdToken();
  const res = await fetch(`https://firebasestorage.googleapis.com/v0/b/${bucket}/o/${encodeURIComponent(path)}?alt=media`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`Storage download failed: ${res.status}`);
  return res.blob();
};

/** True when a file exists at `path` (signed in, metadata only). Throws on errors other than "not found". */
export const storageObjectExists = async (path: string): Promise<boolean> => {
  const user = auth?.currentUser ?? null;
  const bucket = firebaseConfig?.storageBucket;
  if (!user || !bucket) throw new Error('Not signed in');
  const token = await user.getIdToken();
  const res = await fetch(`https://firebasestorage.googleapis.com/v0/b/${bucket}/o/${encodeURIComponent(path)}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (res.status === 404) return false;
  if (!res.ok) throw new Error(`Storage lookup failed: ${res.status}`);
  return true;
};

/** Delete the file at `path` (signed-in SDK). Office only for inspection photos (storage.rules). */
export const deleteStorageObject = async (path: string): Promise<void> => {
  if (!storage) throw new Error('Firebase Storage is not initialized');
  await deleteObject(ref(storage, path));
};

/**
 * The Storage path inside one of our download URLs, or null when the URL isn't one: another
 * host or bucket, no object path, or a `..` segment.
 */
export const storagePathFromUrl = (fileUrl: string, bucket: string): string | null => {
  let url: URL;
  try {
    url = new URL(fileUrl);
  } catch {
    return null;
  }
  if (url.hostname !== 'firebasestorage.googleapis.com') return null;
  const match = url.pathname.match(/^\/v0\/b\/([^/]+)\/o\/(.+)$/);
  if (!match) return null;
  let path: string;
  try {
    if (decodeURIComponent(match[1]) !== bucket) return null;
    path = decodeURIComponent(match[2]);
  } catch {
    return null;
  }
  if (!path || path.split('/').some(s => s === '..' || s === '.' || s === '')) return null;
  return path;
};

/**
 * Delete a file from Firebase Storage given its download URL.
 * Safely no-ops if URL is invalid or bucket/path cannot be derived.
 * `onlyUnder`: when the link came from a record crew can write, the folder(s) it must sit
 * in. A link pointing anywhere else is left alone, so a crafted link can't make an office
 * delete remove some other file (checkup #31).
 */
export const deleteFileByUrl = async (fileUrl: string, onlyUnder?: string[]): Promise<void> => {
  if (onlyUnder) {
    const bucket = firebaseConfig?.storageBucket;
    const path = bucket ? storagePathFromUrl(fileUrl, bucket) : null;
    if (!path || !onlyUnder.some(prefix => path.startsWith(prefix))) {
      logger.warn('[storage] deleteFileByUrl: file is outside the expected folder, skipping delete');
      return;
    }
  }
  if (!storage) return;
  if (!fileUrl) return;

  let url: URL;
  try {
    url = new URL(fileUrl);
  } catch {
    logger.warn('[storage] deleteFileByUrl: invalid URL, skipping delete');
    return;
  }

  // Only handle Firebase Storage download URLs we generated
  if (!url.hostname.includes('firebasestorage.googleapis.com')) {
    logger.warn('[storage] deleteFileByUrl: non-Firebase URL, skipping delete');
    return;
  }

  const bucket = firebaseConfig?.storageBucket;
  if (!bucket) {
    logger.warn('[storage] deleteFileByUrl: storage bucket not configured');
    return;
  }

  const pathSegment = url.pathname;
  const marker = '/o/';
  const idx = pathSegment.indexOf(marker);
  if (idx === -1) {
    logger.warn('[storage] deleteFileByUrl: could not locate object path in URL');
    return;
  }

  const encodedPath = pathSegment.substring(idx + marker.length);
  const cleanedEncodedPath = encodedPath.replace(/^\/+/, '');
  const decodedPath = decodeURIComponent(cleanedEncodedPath);

  try {
    const storageRef = ref(storage, decodedPath);
    await deleteObject(storageRef);
  } catch (err) {
    console.error('[storage] deleteFileByUrl: failed to delete object', err);
  }
};

const safePathSegment = (s: string): string =>
  s.replace(/[^a-zA-Z0-9._-]/g, '_').slice(0, 120);

export const getPmcInvoicingGuidePath = (pmcId: string, fileName: string): string => {
  const safeId = safePathSegment(pmcId || 'pmc');
  const safeName = safePathSegment(fileName || 'guide');
  const ts = Date.now();
  return `pmc-invoicing-guides/${safeId}/guide_${ts}_${safeName}`;
};

export const getPropertyInvoicingGuidePath = (propertyId: string, fileName: string): string => {
  const safeId = safePathSegment(propertyId || 'property');
  const safeName = safePathSegment(fileName || 'guide');
  const ts = Date.now();
  return `property-invoicing-guides/${safeId}/guide_${ts}_${safeName}`;
};

/** Vehicle attachment path pattern: vehicles/{vehicleId}/attachments|serviceRecords|odometerLogs/... */
export type VehicleAttachmentEntityType = 'vehicle' | 'serviceRecord' | 'odometerLog';

/**
 * Build Storage path for a vehicle-related attachment.
 * - vehicle: vehicles/{vehicleId}/attachments/{attachmentId}_{fileName}
 * - serviceRecord: vehicles/{vehicleId}/serviceRecords/{entityId}/{attachmentId}_{fileName}
 * - odometerLog: vehicles/{vehicleId}/odometerLogs/{entityId}/{attachmentId}_{fileName}
 */
export function getVehicleAttachmentStoragePath(
  vehicleId: string,
  entityType: VehicleAttachmentEntityType,
  entityId: string,
  attachmentId: string,
  fileName: string
): string {
  const safe = (s: string) => s.replace(/[^a-zA-Z0-9._-]/g, '_').slice(0, 120);
  const base = `vehicles/${vehicleId}`;
  if (entityType === 'vehicle') {
    return `${base}/attachments/${attachmentId}_${safe(fileName)}`;
  }
  if (entityType === 'serviceRecord') {
    return `${base}/serviceRecords/${entityId}/${attachmentId}_${safe(fileName)}`;
  }
  return `${base}/odometerLogs/${entityId}/${attachmentId}_${safe(fileName)}`;
}

/**
 * Upload a file for a vehicle attachment and return the download URL.
 * Caller should then create the vehicleAttachments doc with this URL.
 */
export const uploadVehicleAttachment = async (
  vehicleId: string,
  entityType: VehicleAttachmentEntityType,
  entityId: string,
  attachmentId: string,
  file: File
): Promise<string> => {
  const path = getVehicleAttachmentStoragePath(vehicleId, entityType, entityId, attachmentId, file.name || 'file');
  return uploadFile(path, file);
};

/**
 * Certificate photo/scan for a crew certification:
 * crewCertifications/{userId}/{certId}_{timestamp}_{fileName}. A fresh name per
 * upload, so replacing a photo never overwrites (overwrite is owner/admin only).
 */
export const uploadCrewCertificationPhoto = async (userId: string, certId: string, file: File): Promise<string> => {
  const path = `crewCertifications/${safePathSegment(userId)}/${safePathSegment(certId)}_${Date.now()}_${safePathSegment(file.name || 'certificate')}`;
  return uploadFile(path, file);
};

/** Document for a company-level credential: firmCredentials/{credId}/{timestamp}_{fileName}. */
export const uploadFirmCredentialPhoto = async (credId: string, file: File): Promise<string> => {
  const path = `firmCredentials/${safePathSegment(credId)}/${Date.now()}_${safePathSegment(file.name || 'certificate')}`;
  return uploadFile(path, file);
};
